/**
 * Filtro de responsáveis (Kanban, tarefas, conversas).
 * Chip ou ícone; a lista só muda ao Aplicar.
 */
import React, { useState, useRef, useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { UserIcon } from '../../icons';
import { CrmUser } from '../../../types';
import { useClickOutside } from '../../../hooks/useClickOutside';
import { useDropdownPosition } from '../../../hooks/useDropdownPosition';
import { cnFilterChip, type FilterChipSurface } from './filterChipStyles';
import { cn } from '@/lib/utils';
import AgentsFilterPanel from './AgentsFilterPanel';

interface AgentsFilterProps {
    users: CrmUser[];
    selectedAgentIds: string[];
    onApply: (ids: string[]) => void;
    currentUserId?: string;
    variant?: 'chip' | 'icon';
    surface?: FilterChipSurface;
}

const AgentsFilter: React.FC<AgentsFilterProps> = ({
    users,
    selectedAgentIds,
    onApply,
    currentUserId,
    variant = 'chip',
    surface = 'segment',
}) => {
    const searchId = useId();
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [draftIds, setDraftIds] = useState<string[]>([]);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const { position, calculatePosition } = useDropdownPosition(triggerRef, {
        offsetY: 8,
        panelWidth: 320,
        viewportPadding: 16,
    });
    useClickOutside(panelRef, () => setIsOpen(false), isOpen, [triggerRef]);

    useEffect(() => {
        if (isOpen) setDraftIds(selectedAgentIds);
    }, [isOpen, selectedAgentIds]);

    const visible = users.filter((u) => u.name.toLowerCase().includes(query.toLowerCase()));
    const selected = selectedAgentIds.length > 0;
    const isIcon = variant === 'icon';
    const filterLabel = isIcon ? 'Filtrar por usuário' : 'Filtrar por responsável';

    const close = (ids?: string[]) => {
        if (ids) onApply(ids);
        setIsOpen(false);
    };

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                aria-expanded={isOpen}
                aria-haspopup="dialog"
                aria-pressed={selected}
                title={isIcon ? filterLabel : undefined}
                aria-label={filterLabel}
                className={isIcon
                    ? cn('btn-icon toolbar-icon-btn shrink-0', selected && 'toolbar-icon-btn--active')
                    : cnFilterChip(selected, surface)
                }
                onClick={() => {
                    if (!isOpen) calculatePosition();
                    setIsOpen((open) => !open);
                }}
            >
                <UserIcon
                    className={isIcon
                        ? 'h-5 w-5 shrink-0'
                        : `h-3.5 w-3.5 shrink-0 transition-colors ${selected ? 'text-primary' : 'text-muted group-hover:text-primary'}`
                    }
                    aria-hidden
                />
                {!isIcon && (
                    <span className="min-w-0 truncate">
                        {selected ? (
                            <>
                                <span className="md:hidden">{selectedAgentIds.length}</span>
                                <span className="hidden md:inline">
                                    {`${selectedAgentIds.length} selecionado${selectedAgentIds.length > 1 ? 's' : ''}`}
                                </span>
                            </>
                        ) : (
                            <>
                                <span className="md:hidden">Resp.</span>
                                <span className="hidden md:inline">Responsável</span>
                            </>
                        )}
                    </span>
                )}
            </button>

            {isOpen && createPortal(
                <AgentsFilterPanel
                    panelRef={panelRef}
                    position={position}
                    searchId={searchId}
                    query={query}
                    onQueryChange={setQuery}
                    users={visible}
                    draftIds={draftIds}
                    currentUserId={currentUserId}
                    onToggleUser={(id) => setDraftIds((prev) => (
                        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
                    ))}
                    onClear={() => close([])}
                    onApply={() => close(draftIds)}
                />,
                document.body,
            )}
        </>
    );
};

export default AgentsFilter;
